import { useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/query-client";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { ContactFormContent } from "@/components/contacts/contact-form-content";
import type { CreateContactFormData } from "@/components/contacts/contact-form-dialog";

/**
 * Standalone page rendered inside the contact form popup window.
 *
 * URL params:
 *   mode        "create" | "edit"
 *   contact_id  (edit only) — the contact's UUID
 *   title       Dialog title text
 *   submit_label Button label
 *   <field>     One param per form field (all 14 are primitives)
 *
 * On success, posts { type: "contact-saved" } to window.opener then closes.
 */
function ContactFormPopupInner() {
  const { toast } = useToast();
  const params = new URLSearchParams(window.location.search);

  const mode = params.get("mode") ?? "create";
  const contactId = params.get("contact_id") ?? undefined;
  const title = params.get("title") ?? (mode === "edit" ? "Edit Contact" : "Create New Contact");
  const submitLabel = params.get("submit_label") ?? (mode === "edit" ? "Save Changes" : "Create Contact");

  const defaultValues: Partial<CreateContactFormData> = {
    firstName:    params.get("firstName")    ?? "",
    lastName:     params.get("lastName")     ?? "",
    email:        params.get("email")        ?? "",
    phone:        params.get("phone")        ?? "",
    mobilePhone:  params.get("mobilePhone")  ?? "",
    officePhone:  params.get("officePhone")  ?? "",
    telephone:     params.get("telephone")     ?? "",
    deskExtension: params.get("deskExtension") ?? "",
    contactType:   (params.get("contactType")  ?? "claimant") as CreateContactFormData["contactType"],
    entityType:   (params.get("entityType")  ?? "individual") as CreateContactFormData["entityType"],
    companyId:    params.get("companyId")    ?? null,
    address:      params.get("address")      ?? "",
    city:         params.get("city")         ?? "",
    state:        params.get("state")        ?? "",
    zip:          params.get("zip")          ?? "",
    notes:        params.get("notes")        ?? "",
  };

  const [isPending, setIsPending] = useState(false);

  async function handleSubmit(data: CreateContactFormData) {
    setIsPending(true);
    try {
      let resolvedContactId = contactId;
      if (mode === "edit" && contactId) {
        await apiRequest("PUT", `/api/contacts/${contactId}`, data);
      } else {
        const created = await apiRequest("POST", "/api/contacts", data);
        const createdContact = await created.json();
        resolvedContactId = createdContact?.id;
      }
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: "contact-saved", mode, contactId: resolvedContactId },
          window.location.origin
        );
      }
      window.close();
    } catch (err: any) {
      toast({
        title: "Error",
        description: err.message || "Failed to save contact",
        variant: "destructive",
      });
    } finally {
      setIsPending(false);
    }
  }

  function handleCancel() {
    window.close();
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-lg mx-auto">
      <h1 className="text-base font-semibold mb-3">{title}</h1>
      <ContactFormContent
        defaultValues={defaultValues}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isPending={isPending}
        submitLabel={submitLabel}
        staticMount
        contactId={mode === "edit" ? contactId : undefined}
      />
      </div>
    </div>
  );
}

export function ContactFormPopupPage() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <ContactFormPopupInner />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
